/**
 * App-side façade over the `track.js` embed script (`src/gnothi/web/src/track-script/track.ts`,
 * served from this app's own origin at `/track.js`).
 *
 * Every emit goes through here rather than touching `window.gnothi` directly, for two reasons:
 *   1. the script is absent whenever it should be — non-prod stages have no site id, the file
 *      is a deploy-time-only build output, and an opted-out visitor gets a no-op API — so a
 *      caller must never branch on that, and
 *   2. these five keys are FROZEN (arc decision D26) and enumerated by a sibling query lane in
 *      a separate worktree. A variant spelling silently orphans a dashboard column, so the keys
 *      live in one `const` here and callers reference it by name.
 *
 * Nothing in this module throws: an analytics emit must never take down the payment submit or
 * the signup handler it is measuring. The script guards its own bodies too; this is the second
 * belt, covering the case where `window.gnothi` is something unexpected entirely.
 */

/** The complete gnothi CLIENT key set (D26). `page_view` is the protocol pageview, sent by the script. */
export const CLIENT_EVENTS = {
  signup: 'gnothi:funnel:signup',
  payView: 'gnothi:pay:view',
  payIntent: 'gnothi:pay:intent',
  seriesOpen: 'gnothi:funnel:series_open',
} as const

export type ClientEvent = (typeof CLIENT_EVENTS)[keyof typeof CLIENT_EVENTS]

/* ── insider suppression ──────────────────────────────────────────────────────────────────────
 *
 * The owner's friends carry `is_cool` / `is_superuser` and know how to use the product, so their
 * sessions read as conversion this funnel never earned. A KNOWN insider's events are DROPPED here,
 * at the emit seam: nothing is tagged and nothing is filtered downstream, so a suppressed event
 * leaves no row and no marker at all. Only a known one — nothing is ever inferred about a
 * logged-out visitor, and nothing WAITS on an answer. (The `?notrack` opt-out is a separate,
 * privacy-policy-promised feature that lives in the script and is untouched by any of this.)
 *
 * 🔴 STANDING RULE, and this module now satisfies it by importing nothing at all: nothing reachable
 * from here may `import` the store. `app/root.tsx` imports this file, and root is the parent of the
 * six PRERENDERED routes, whose entire module graph Node evaluates during the prerender pass —
 * `@gnothi/shared/store` drags zustand + Amplify in and crashes it (`app/routes.ts:12`,
 * `app/routes/home-shims.ts:5`). The verdict is therefore a raw storage read, which is also the
 * only signal that exists on the prerendered routes: they never mount `Setup`, so they never
 * rehydrate the store even at runtime.
 *
 * ACCEPTED RESIDUAL, measured rather than assumed. `shared/store/storage.ts` debounces the persist
 * write by `WRITE_DEBOUNCE_MS` (2s, and the timer RESETS on every `set()`), so on a session whose
 * blob carries no `me` yet — a first load on this device, or a load after `clearPersistedData()` —
 * the flags are invisible here for the boot burst plus that debounce, and every emit inside it is
 * sent. In practice that is the first-paint `trackPageView()`. `gnothi:pay:intent` and
 * `gnothi:pay:view` both sit behind `PurchaseDialog`, which a user reaches only through Account →
 * Premium → open, so neither can fire inside the window.
 *
 * 🔴 `seriesOpen` IS reachable inside it — one click from first paint — so the residual lands on the
 * GOLDEN-FEATURE metric, the discovery rate the whole "users aren't paying, why?" question turns on.
 * Its rate is unknown and cannot be recovered retroactively, so that number is read as a TREND
 * across tweaks and never as an absolute; the consumer-side caveat lives where the number is read,
 * `docs/claude/agents/upkeep-gnothi.md` § Real users only. ACCEPTED, not pending: ~95% of usage was
 * `is_cool`/`is_superuser`, and clearing that contaminant is worth this leak. A future pass could
 * close it properly — POSSIBLE, not owed, and priced against that 95% first.
 */

type Flags = {is_cool?: boolean | null; is_superuser?: boolean | null}

/**
 * `me` as the store last persisted it (`shared/store` puts it in `partialize`). Read as raw JSON
 * rather than imported, per the 🔴 above. A partialize shape change degrades this to `undefined`,
 * which reads as "we cannot answer" and therefore emits — it fails OPEN, as every arm here does.
 */
function persistedMe(): Flags | undefined {
  const raw = localStorage.getItem('gnothi-store')
  return raw ? JSON.parse(raw)?.state?.user?.me ?? undefined : undefined
}

/**
 * Read per emit rather than resolved once per page load. That keeps the module STATELESS — no
 * cache to invalidate, no test-only reset hook, no order-dependent cases — and it is what lets a
 * verdict that only becomes readable mid-session (the debounce above) start suppressing from the
 * next emit onward instead of never. The cost is one storage read + parse per emit, and the
 * suppressible emits are page views on navigation plus a handful of clicks, not a render-path call.
 */
function isKnownInsider(): boolean {
  try {
    const me = persistedMe()
    return !!(me && (me.is_cool || me.is_superuser))
  } catch {
    // No `window`, storage blocked in private mode, or a corrupt blob. We cannot know, so we do
    // not guess: leaking an insider beats losing every real visitor in private mode.
    return false
  }
}

function send(fn: () => void): void {
  try { fn() } catch { /* never break the caller */ }
}

/** The one gate every suppressible emit passes through. */
function gate(fn: () => void): void {
  if (isKnownInsider()) return
  send(fn)
}

/**
 * Emit one of the frozen client events.
 *
 * `properties` must be a CLOSED LOW-CARDINALITY ENUM, same rule as the server-side `track()`:
 * never free text, ids, paths, or anything a person typed.
 */
export function trackEvent(name: ClientEvent, properties?: Record<string, string | number | boolean>): void {
  gate(() => window.gnothi?.event(name, properties))
}

/** Record a navigation. The script owns the redaction, the deferral and the de-duplication. */
export function trackPageView(): void {
  gate(() => window.gnothi?.pageView())
}

/**
 * One-shot signup attribution. The landing route shape and referrer host it carries were
 * captured in the script's closure at page load and live in memory only — this call cannot
 * read or write storage, and a second call is a no-op.
 *
 * Deliberately NOT gated: it fires on a brand-new account, which by construction carries no flag
 * yet, so the gate could never do anything but cost a storage read.
 */
export function trackSignup(): void {
  try {
    window.gnothi?.signup()
  } catch {
    /* never break the caller */
  }
}
